import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Reveal } from '../components/Reveal';
import { brands } from '../mockData';

const AboutPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white pt-24 md:pt-32">

      {/* Header */}
      <section className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto mb-12 md:mb-16">
        <Reveal>
          <button
            data-testid="about-back-button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-[#B8960C] transition-colors duration-300 mb-12"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back to Home
          </button>

          <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#B8960C] mb-6">
            Our Story
          </p>
          <h1
            data-testid="about-page-heading"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] uppercase leading-tight"
            style={{ letterSpacing: '0.1em' }}
          >
            Built on Vision
          </h1>
          <div className="w-16 h-px bg-[#B8960C] mt-8 opacity-40"></div>
        </Reveal>
      </section>

      {/* Key Facts — top of page */}
      <section className="border-y border-black/5 mb-20 md:mb-28">
        <div className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
          <Reveal>
            <div className="grid grid-cols-2 md:grid-cols-5 py-12 md:py-16">
              {[
                { label: 'Founded', value: '2017' },
                { label: 'Headquarters', value: 'London' },
                { label: 'Brands', value: '3' },
                { label: 'Clients', value: '500+' },
                { label: 'Reach', value: 'Global' }
              ].map((fact, i) => (
                <div
                  key={fact.label}
                  className={`text-center py-6 md:py-0 ${i < 4 ? 'md:border-r md:border-black/5' : ''}`}
                >
                  <p className="font-heading text-3xl md:text-4xl text-[#1A1A1A] mb-2" style={{ letterSpacing: '0.05em' }}>
                    {fact.value}
                  </p>
                  <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#9CA3AF]">
                    {fact.label}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* About Copy */}
      <section className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto mb-20 md:mb-28">
        <div className="max-w-3xl">
          <Reveal>
            <p
              data-testid="about-lead-text"
              className="text-base text-[#6B7280] font-light leading-relaxed mb-6"
            >
              Founded in London in 2017, Hinton Enterprises began as a digital publishing business and has since grown into a multi-brand company operating across media, marketing, retail representation and creative services.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-base text-[#6B7280] font-light leading-relaxed">
              Today, we work with businesses and brands from around the world, helping them build their presence, reach new audiences and create opportunities within the UK and beyond. Across every part of the business, our focus remains the same: delivering professional, effective and results-driven solutions for the people and organisations we work with.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Our Brands Grid */}
      <section className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto pb-24 md:pb-32 lg:pb-48">
        <Reveal>
          <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#B8960C] mb-6">
            What We Do
          </p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] uppercase leading-tight mb-16"
            style={{ letterSpacing: '0.1em' }}
          >
            Three Brands, One Mission
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
          {brands.map((brand, i) => (
            <Reveal key={brand.id} delay={i * 0.08}>
              <div data-testid={`about-brand-${brand.slug}`}>
                <h3
                  className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-2"
                  style={{ letterSpacing: '0.05em' }}
                >
                  {brand.name}
                </h3>
                <p className="text-xs text-[#B8960C] font-medium tracking-wide uppercase mb-4">
                  {brand.tagline}
                </p>
                <p className="text-sm text-[#6B7280] font-light leading-relaxed">
                  {brand.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
